import { WuserCandRestrictType } from '@ats/api/cm';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AppUserType } from './app-user-type.enum';
import { AppUser } from './app-user.interface';
import { Checkpoints } from './checkpoints.interface';

export class Guard {
	private results: boolean[] = [];
	public against: Checkpoints;

	constructor(against: Checkpoints) {
		this.against = against;
	}

	public static getFromUser$(user$: Observable<AppUser>) {
		return user$.pipe(
			filter(user => user !== null),
			map(user => user as AppUser),
			map(this.getCheckpointsFromUser),
			map(checkpoints => new Guard(checkpoints))
		);
	}

	public static getCheckpointsFromUser(user: AppUser) {
		const { type, features, permissions, admin, internal, candRestrictType, qlik, localizationEnabled } = user;
		return {
			userType: type,
			features,
			permissions,
			admin,
			internal,
			candRestrictType,
			qlik,
			localizationEnabled,
		} as Checkpoints;
	}

	public get all() {
		return this.results.every(result => result);
	}

	public get any() {
		return this.results.some(result => result);
	}

	public get none() {
		return !this.results.some(result => result);
	}

	public static HasEveryFeature = (features: string[], against: string[]) => {
		return !features.length || features.every(feat => against.includes(feat));
	};

	public static HasSomeFeatures = (features: string[], against: string[]) => {
		return features.some(feat => against.includes(feat));
	};

	public static HasEveryPermission = (permissions: string[], against: string[]) => {
		return !permissions.length || permissions.every(perm => against.includes(perm));
	};

	public static HasSomePermissions = (permissions: string[], against: string[]) => {
		return permissions.some(perm => against.includes(perm));
	};

	public reset() {
		this.results = [];
		return this;
	}

	public canLocalize = () => {
		this.results.push(this.against.localizationEnabled);
		return this;
	};

	public isCandidate = () => {
		const result = this.against.userType === AppUserType.CANDIDATE;
		this.results.push(result);
		return this;
	};

	public isNotCandidate = () => {
		const result = this.against.userType !== AppUserType.CANDIDATE;
		this.results.push(result);
		return this;
	};

	public isWebUser = () => {
		const result = this.against.userType === AppUserType.WEB_USER;
		this.results.push(result);
		return this;
	};

	public isNotWebUser = () => {
		const result = this.against.userType !== AppUserType.WEB_USER;
		this.results.push(result);
		return this;
	};

	public isAdmin = () => {
		const result = this.against.admin !== null ? this.against.admin : false;
		this.results.push(result || false);
		return this;
	};

	public notAdmin = () => {
		const result = this.against.admin !== null ? !this.against.admin : true;
		this.results.push(result);
		return this;
	};

	public isInternal = () => {
		const result = this.against.internal !== null ? this.against.internal : false;
		this.results.push(result || false);
		return this;
	};

	public notInternal = () => {
		const result = this.against.internal !== null ? !this.against.internal : true;
		this.results.push(result);
		return this;
	};

	public isAdminOrInternal = () => {
		const result1 = this.against.admin !== null ? this.against.admin : false;
		const result2 = this.against.internal !== null ? this.against.internal : false;
		this.results.push(result1 || result2 || false);
		return this;
	};

	public isRestricted = () => {
		const result =
			this.against.candRestrictType !== null
				? this.against.candRestrictType === WuserCandRestrictType.Restricted
				: false;
		this.results.push(result);
		return this;
	};

	public notRestricted = () => {
		const result =
			this.against.candRestrictType !== null
				? this.against.candRestrictType !== WuserCandRestrictType.Restricted
				: true;
		this.results.push(result);
		return this;
	};

	public isFiltered = () => {
		const result =
			this.against.candRestrictType !== null
				? this.against.candRestrictType === WuserCandRestrictType.Filtered
				: false;
		this.results.push(result);
		return this;
	};

	public notFiltered = () => {
		const result =
			this.against.candRestrictType !== null
				? this.against.candRestrictType !== WuserCandRestrictType.Filtered
				: true;
		this.results.push(result);
		return this;
	};

	public isUnrestricted = () => {
		const result =
			this.against.candRestrictType !== null
				? this.against.candRestrictType === WuserCandRestrictType.Unrestricted
				: false;
		this.results.push(result);
		return this;
	};

	public notUnrestricted = () => {
		const result =
			this.against.candRestrictType !== null
				? this.against.candRestrictType !== WuserCandRestrictType.Unrestricted
				: true;
		this.results.push(result);
		return this;
	};

	public hasQlik = () => {
		const result = this.against.qlik;
		this.results.push(result || false);
		return this;
	};

	public custom = (customGuard: (guard: Guard) => boolean) => {
		const result = customGuard(this);
		this.results.push(result);
		return this;
	};

	public everyFeature = (features: string[]) => {
		const result = this.against.features ? Guard.HasEveryFeature(features, this.against.features) : false;
		this.results.push(result);
		return this;
	};

	public everyPermission = (permissions: string[]) => {
		if (this.against.admin || this.against.userType === AppUserType.CANDIDATE) {
			this.results.push(true);
			return this;
		}
		const result = this.against.permissions
			? Guard.HasEveryPermission(permissions, this.against.permissions)
			: false;
		this.results.push(result);
		return this;
	};

	public someFeatures = (features: string[]) => {
		const result = this.against.features ? Guard.HasSomeFeatures(features, this.against.features) : false;
		this.results.push(result);
		return this;
	};

	public somePermissions = (permissions: string[]) => {
		if (this.against.admin || this.against.userType === AppUserType.CANDIDATE) {
			this.results.push(true);
			return this;
		}
		const result = this.against.permissions
			? Guard.HasSomePermissions(permissions, this.against.permissions)
			: false;
		this.results.push(result);
		return this;
	};

	public noFeature = (features: string[]) => {
		const result =
			this.against.features && this.against.features.length
				? !Guard.HasSomeFeatures(features, this.against.features)
				: false;
		this.results.push(result);
		return this;
	};

	public noPermission = (permissions: string[]) => {
		const result =
			this.against.permissions && this.against.permissions.length
				? !Guard.HasSomePermissions(permissions, this.against.permissions)
				: false;
		this.results.push(result);
		return this;
	};
}
